import React from 'react';

const errorMessage = (scope) => (
  scope === 'global'
    ? 'De toepassing kon niet worden geladen. Vernieuw de pagina om opnieuw te proberen.'
    : 'Deze pagina kon niet worden geladen. Probeer het opnieuw of ga terug.'
);

const boundaryCode = (scope) => (
  scope === 'page' ? 'zoea.page.render_failure' : 'zoea.global.render_failure'
);

export const reportRenderError = ({ scope, error, info, onError, environment = process.env.NODE_ENV, log = console.error }) => {
  const code = boundaryCode(scope);

  try {
    if (onError) {
      if (environment === 'development') {
        onError(error, info);
      } else {
        onError({ code });
      }
    } else if (environment === 'development') {
      log(`[Zoea] ${scope || 'app'} render error`, error, info);
    } else {
      log(`[Zoea] ${code}`);
    }
  } catch {
    // Reporting must never replace the safe fallback.
  }
};

export const SafeErrorFallback = ({ scope, onRetry, onReload, onBack }) => {
  const canGoBack = typeof window !== 'undefined' && window.history.length > 1;

  return (
    <main
      role="alert"
      aria-labelledby="zoea-error-title"
      style={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        padding: '2rem',
        background: '#f8fafc',
        color: '#0f172a',
        fontFamily: 'system-ui, sans-serif',
      }}
    >
      <section style={{ maxWidth: '36rem', textAlign: 'center' }}>
        <p style={{ marginBottom: '0.75rem', color: '#0f766e', fontWeight: 700 }}>Zoea</p>
        <h1 id="zoea-error-title" style={{ margin: 0, fontSize: '1.5rem' }}>Er ging iets mis</h1>
        <p style={{ margin: '1rem 0 1.5rem', lineHeight: 1.6 }}>{errorMessage(scope)}</p>
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '0.75rem' }}>
          <button type="button" onClick={onRetry}>Opnieuw proberen</button>
          <button type="button" onClick={onReload}>Pagina vernieuwen</button>
          {canGoBack && <button type="button" onClick={onBack}>Terug</button>}
        </div>
      </section>
    </main>
  );
};

export class ErrorBoundary extends React.Component {
  state = { error: null };

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    reportRenderError({
      scope: this.props.scope,
      error,
      info,
      onError: this.props.onError,
    });
  }

  componentDidUpdate(previousProps) {
    if (this.state.error && this.props.resetKey !== previousProps.resetKey) {
      this.setState({ error: null });
    }
  }

  reset = () => {
    this.setState({ error: null });
  };

  reload = () => {
    try {
      if (this.props.onReload) {
        this.props.onReload();
      } else if (typeof window !== 'undefined' && typeof window.location?.reload === 'function') {
        window.location.reload();
      }
    } catch {
      // A browser recovery action must not replace the fallback.
    }
  };

  back = () => {
    try {
      if (this.props.onBack) {
        this.props.onBack();
      } else if (typeof window !== 'undefined' && window.history.length > 1) {
        window.history.back();
      }
    } catch {
      // A browser recovery action must not replace the fallback.
    }
  };

  render() {
    if (this.state.error) {
      return (
        <SafeErrorFallback
          scope={this.props.scope}
          onRetry={this.reset}
          onReload={this.reload}
          onBack={this.back}
        />
      );
    }

    return this.props.children;
  }
}

export const GlobalErrorBoundary = ({ children, onError, onReload, onBack }) => (
  <ErrorBoundary scope="global" onError={onError} onReload={onReload} onBack={onBack}>{children}</ErrorBoundary>
);

export const PageErrorBoundary = ({ children, resetKey, onError, onReload, onBack }) => (
  <ErrorBoundary scope="page" resetKey={resetKey} onError={onError} onReload={onReload} onBack={onBack}>{children}</ErrorBoundary>
);

export default ErrorBoundary;
